/**
 * Die Offline-Operation nach `04` §4.12 — was der Monteur ohne Netz erzeugt.
 *
 * Auf der Baustelle gibt es kein Netz, und die Monteur-App muss trotzdem
 * benutzbar sein. Jede Änderung wird deshalb zuerst lokal in eine Outbox
 * geschrieben und später zugestellt. Was dabei alles schiefgehen kann, steht
 * in `04` §4.12; dieses Modul bildet die Regeln ab, nicht den Transport.
 *
 * **Kein Speicher, kein Netz, keine Verschlüsselung hier.** Nach ADR-016
 * bleibt dieses Paket reines TypeScript, damit eine native App denselben Code
 * benutzen kann (`02` §2.13). Wo die Outbox liegt und womit sie verschlüsselt
 * ist, entscheidet `PWA-003`; dass eine Operation gültig ist und ob sie
 * angenommen werden darf, entscheidet dieses Modul.
 */

import {
  istZustand,
  uebergangErlaubt,
  type EinsatzZustand,
} from "./einsatzzustand.js";

/**
 * Was eine Offline-Operation ändern kann.
 *
 * Die Unterscheidung ist die Grundlage der Konfliktregeln: `04` §4.12 behandelt
 * additive Notizen anders als Statuswechsel und beide anders als einen
 * geänderten Freitext. Ein Mutationstyp `aenderung` für alles würde die Regeln
 * unanwendbar machen.
 */
export const MUTATION = [
  /** Notiz, Foto, Zeitposten, Materialposten — kommt hinzu, ersetzt nichts. */
  "additiv",
  /** Wechsel des Einsatzzustands. Prüft gegen den Automaten aus ADR-045. */
  "zustand",
  /** Ersetzt einen vorhandenen Freitext. Braucht eine Baseline. */
  "freitext",
] as const;

export type Mutation = (typeof MUTATION)[number];

export interface Offlineoperation {
  /** Lokal vergeben, bevor es Netz gibt. Bleibt über Zustellversuche stabil. */
  readonly id: string;
  readonly tenantId: string;
  readonly actorRef: string;
  /** Worauf sie wirkt, in der Zeichenkettenform aus `04` §4.2. */
  readonly resourceRef: string;
  /**
   * Der Stand, auf dem der Monteur gearbeitet hat.
   *
   * Ohne ihn ließe sich nicht sagen, ob inzwischen jemand anders etwas
   * geändert hat — jede Zustellung würde blind überschreiben.
   */
  readonly baselineVersion: number;
  readonly mutation: Mutation;
  /** Verschlüsselt; dieses Modul sieht nur, dass etwas da ist. */
  readonly payload: string;
  /** ISO-8601 in UTC. */
  readonly erstelltAm: string;
  readonly retryZaehler: number;
  /**
   * Fängt die doppelte Zustellung ab.
   *
   * Getrennt von `id`, obwohl beide eine Operation kennzeichnen: Die `id`
   * gehört dem Gerät, der Schlüssel dem Vorgang. Zwei Geräte, die denselben
   * Arbeitsschritt abhaken, erzeugen zwei ids und denselben Schlüssel.
   */
  readonly idempotencyKey: string;
}

export type Entscheidung =
  /** Zustellen. */
  | "annehmen"
  /** Schon verarbeitet — verwerfen, ohne Aufhebens. */
  | "doppelt"
  /** Ein Mensch muss entscheiden. Landet im Büro, nicht im Nichts. */
  | "konflikt"
  /** Endgültig abgelehnt; erneutes Zustellen ändert nichts. */
  | "abgelehnt";

export interface Serverstand {
  /** Aktueller Zustand des Einsatzes, falls die Ressource einer ist. */
  readonly zustand?: EinsatzZustand;
  readonly version: number;
  /** Schon verarbeitete Idempotenzschlüssel dieser Ressource. */
  readonly verarbeitet: readonly string[];
  /** Darf der Handelnde diese Ressource überhaupt noch ändern? */
  readonly erlaubt: boolean;
}

export interface Befund {
  readonly entscheidung: Entscheidung;
  readonly grund: string;
  /**
   * Was mit bereits erfassten lokalen Daten geschehen soll.
   *
   * `04` §4.12 verlangt bei fehlender Berechtigung ausdrücklich, lokal nicht
   * mehr erlaubte Inhalte zu **entfernen**. Bei allen anderen Ablehnungen
   * gilt das Gegenteil: Der Monteur hat gearbeitet, und seine Erfassung
   * verschwindet nicht, weil ein Zustandswechsel nicht durchging.
   */
  readonly lokalLoeschen: boolean;
}

/**
 * Darf diese Operation zugestellt werden?
 *
 * Die Reihenfolge der Prüfungen ist nicht beliebig:
 *
 *   1. **Berechtigung zuerst.** Wer nicht mehr darf, bekommt keine
 *      Konfliktmeldung über Inhalte, die er nicht mehr sehen soll.
 *   2. **Dann die Dopplung.** Eine bereits verarbeitete Operation ist kein
 *      Konflikt, auch wenn sich der Stand inzwischen geändert hat — sonst
 *      erzeugt jeder Wiederholungsversuch nach einem Netzabbruch eine
 *      Rückfrage ans Büro.
 *   3. **Dann die Fachlogik.**
 */
export function pruefeOperation(op: Offlineoperation, stand: Serverstand): Befund {
  if (!stand.erlaubt) {
    return {
      entscheidung: "abgelehnt",
      grund: "Keine Berechtigung mehr auf dieser Ressource.",
      lokalLoeschen: true,
    };
  }

  if (stand.verarbeitet.includes(op.idempotencyKey)) {
    return {
      entscheidung: "doppelt",
      grund: "Dieser Vorgang wurde bereits verarbeitet.",
      lokalLoeschen: false,
    };
  }

  if (op.mutation === "additiv") {
    // Notizen, Fotos, Zeiten und Material kommen hinzu und ersetzen nichts.
    // Ein veralteter Baselinestand macht sie nicht falsch: Zwei Monteure, die
    // unabhängig je ein Foto anhängen, haben keinen Konflikt, sondern zwei
    // Fotos. Eine Baselineprüfung hier wäre der häufigste Fehlalarm.
    return { entscheidung: "annehmen", grund: "Additive Änderung.",
      lokalLoeschen: false };
  }

  if (op.mutation === "freitext") {
    if (op.baselineVersion === stand.version) {
      return { entscheidung: "annehmen", grund: "Freitext auf aktuellem Stand.",
        lokalLoeschen: false };
    }
    // `04` §4.12: gleichzeitig geänderte Freitexte brauchen eine
    // Benutzerentscheidung. Automatisch zusammenführen wäre die bequeme
    // Antwort und die falsche — bei einer Tätigkeitsnotiz entstünde ein Text,
    // den niemand geschrieben hat.
    return {
      entscheidung: "konflikt",
      grund: `Der Text wurde inzwischen geändert (Stand ${stand.version}, `
        + `bearbeitet wurde ${op.baselineVersion}).`,
      lokalLoeschen: false,
    };
  }

  // Ab hier: Zustandswechsel.
  const ziel = zielZustand(op.payload);
  if (!ziel) {
    return {
      entscheidung: "abgelehnt",
      grund: "Die Operation nennt keinen gültigen Zielzustand.",
      lokalLoeschen: false,
    };
  }
  if (!stand.zustand) {
    return {
      entscheidung: "abgelehnt",
      grund: "Die Ressource führt keinen Einsatzzustand.",
      lokalLoeschen: false,
    };
  }
  if (stand.zustand === ziel) {
    // Nicht als Erfolg durchwinken: Der Zustand stimmt zwar, aber diese
    // Operation hat ihn nicht bewirkt. Als „doppelt" gemeldet, damit die
    // Outbox sie loswird, ohne dass ein zweites Auditereignis entsteht.
    return {
      entscheidung: "doppelt",
      grund: `Der Einsatz steht bereits auf ${ziel}.`,
      lokalLoeschen: false,
    };
  }
  if (!uebergangErlaubt(stand.zustand, ziel)) {
    // Der Fall, für den `02` §2.9 ein Freigabekriterium führt: Ein Abschluss
    // auf einem stornierten Einsatz wird **nicht still übernommen**. Er wird
    // auch nicht weggeworfen — das Büro sieht ihn und entscheidet.
    return {
      entscheidung: "konflikt",
      grund: `${stand.zustand} → ${ziel} ist kein gültiger Übergang.`,
      lokalLoeschen: false,
    };
  }
  return { entscheidung: "annehmen", grund: `${stand.zustand} → ${ziel}.`,
    lokalLoeschen: false };
}

/**
 * Der Zielzustand aus der Nutzlast.
 *
 * Die Nutzlast ist im Betrieb verschlüsselt; entschlüsselt wird sie von
 * `PWA-003`, bevor diese Prüfung läuft. Hier steht nur, wie der Zustand darin
 * benannt ist — als schlichte Zeichenkette, damit dieses Modul kein
 * Serialisierungsformat festlegt, das ein anderes Paket dann tragen müsste.
 */
function zielZustand(payload: string): EinsatzZustand | null {
  const wert = payload.trim();
  return istZustand(wert) ? wert : null;
}
